import React, { useState, useEffect, useCallback, useRef, useMemo, Suspense, lazy } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getFirestore, collection, query, orderBy, limit, getDocs, where } from 'firebase/firestore';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import heroBgWebp from '../login-hero.webp';
import heroBgJpg from '../login-hero.jpg';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { useMapSettings } from '../hooks/useMapSettings';
import { usePrimaryColor } from '../hooks/usePrimaryColor';
import SEO from '../components/SEO';
import ScrollToTop from '../components/ScrollToTop';
import { trackSearch } from '../services/analyticsService';
import PreLoader from '../components/PreLoader';
import { FaMapMarkerAlt, FaUsers, FaAward, FaHome, FaChartLine } from 'react-icons/fa';
import HomeHeroSearchForm from '../components/home/HomeHeroSearchForm';
import {
  fetchPropertiesCatalog,
  fetchCondominiumsCatalog,
  fetchHomeFeaturedProperties,
} from '../services/propertiesCatalog';
import HomeLocationSpotsSection from '../components/HomeLocationSpotsSection';
import CtaBanner from '../components/CtaBanner';
import { generatePropertyImageAlt } from '../utils/seo/altText';
import { buildPropertyPath } from '../utils/seo/urlHelpers';

const HomeHeroEditorial = lazy(() => import('../components/home/HomeHeroEditorial'));
const HomeHeroLuxury = lazy(() => import('../components/home/HomeHeroLuxury'));
const HomeHeroFloatingBar = lazy(() => import('../components/HomeHeroFloatingBar'));
const HomeHeroMapbox = lazy(() => import('../components/HomeHeroMapbox'));
const HomeHeroKenBurnsSlideshow = lazy(() => import('../components/home/HomeHeroKenBurnsSlideshow'));

const heroBg = heroBgWebp;

const outlineCtaClassName =
  'inline-flex items-center justify-center w-full px-8 py-3 rounded-full font-medium transition-all duration-300 text-center no-underline';

const outlineCtaStyle = {
  backgroundColor: 'var(--btn-outline-bg, #ffffff)',
  color: 'var(--btn-outline-text, #3B82F6)',
  border: '1px solid var(--btn-outline-border, #3B82F6)',
};

const outlineCtaHoverHandlers = {
  onMouseEnter: (e) => {
    e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
    e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
  },
  onMouseLeave: (e) => {
    e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
    e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
  },
};

function Home() {
  const navigate = useNavigate();
  const { 
    aboutHeroTitle, 
    aboutHeroDescription, 
    aboutHeroImage, 
    aboutHeroInsight1,
    aboutHeroInsight2,
    aboutStat1Name,
    aboutStat1Value,
    aboutStat2Name,
    aboutStat2Value,
    aboutStat3Name,
    aboutStat3Value,
    aboutStat4Name,
    aboutStat4Value,
    feedbacksTitle,
    feedbacksDescription,
    ctaEnabled,
    ctaLayout,
    ctaTitle,
    ctaDescription,
    ctaButtonText,
    ctaImageSource,
    ctaSystemImage,
    ctaImageStyle,
    ctaImage,
    companyName,
    settingsReady,
    homeHeaderImage,
    homeHeaderVideo,
    homeHeaderYoutubeUrl,
    homeHeaderMediaType,
    homeHeaderImagePresentation,
    homeHeaderSlideshowImages,
    homeTitle,
    homeSubtitle,
    homeHeroLayout,
    homeLocationSpotsMode,
    homeLocationSpotsEntity,
    homeLocationSpots,
    // Configurações SEO
    seoMetaTitle,
    seoMetaDescription,
    seoKeywords,
    seoOgTitle,
    seoOgDescription,
    seoOgImage,
    seoCanonicalUrl,
    primaryColor,
  } = useCompanySettings();

  const { mapSettings, loading: mapSettingsLoading } = useMapSettings();
  const mapHomeHeroAllowed =
    !mapSettingsLoading &&
    mapSettings.homeMapHeroEnabled === true &&
    mapSettings.provider === 'mapbox' &&
    Boolean(String(mapSettings.mapboxToken || '').trim());
  const resolvedHomeHeroLayout =
    (homeHeroLayout || 'padrao') === 'mapaMapbox' && !mapHomeHeroAllowed
      ? 'padrao'
      : homeHeroLayout || 'padrao';
  const heroOverlapBottomPadding =
    resolvedHomeHeroLayout === 'barraFlutuante' || resolvedHomeHeroLayout === 'mapaMapbox';

  const homePageTitle = useMemo(() => {
    if (seoMetaTitle?.trim()) return seoMetaTitle;
    const name = (companyName || '').trim();
    if (name) return `${name} - Imóveis | Compra, Venda e Aluguel`;
    if (!settingsReady) return 'Carregando...';
    return 'Imobiliária - Imóveis | Compra, Venda e Aluguel';
  }, [seoMetaTitle, companyName, settingsReady]);
  
  // Inicializa as cores primárias CSS
  usePrimaryColor();
  
  // Função para extrair ID do YouTube de forma segura
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // Função para criar URL do YouTube com qualidade otimizada
  const createYouTubeUrl = (videoId, quality = 'hd1080') => {
    const baseUrl = `https://www.youtube.com/embed/${videoId}`;
    const params = [
      'autoplay=1',
      'loop=1',
      `playlist=${videoId}`,
      'mute=1',
      'controls=0',
      'showinfo=0',
      'rel=0',
      'modestbranding=1',
      'iv_load_policy=3',
      `vq=${quality}` // Qualidade específica
    ];
    return `${baseUrl}?${params.join('&')}`;
  };

  // Hook para gerenciar qualidade do YouTube
  const [youtubeQuality, setYoutubeQuality] = useState('hd1080');
  
  // Qualidades em ordem de prioridade
  const qualityFallback = ['hd1080', 'hd720', 'large', 'medium', 'small'];

  // Reset quality when YouTube URL changes
  useEffect(() => {
    if (homeHeaderMediaType === 'youtube' && homeHeaderYoutubeUrl) {
      setYoutubeQuality('hd1080');
    }
  }, [homeHeaderMediaType, homeHeaderYoutubeUrl]);

  const homeHeaderSlideshowUrls = useMemo(() => {
    if (!Array.isArray(homeHeaderSlideshowImages)) return [];
    return homeHeaderSlideshowImages
      .filter((u) => typeof u === 'string' && u.trim())
      .slice(0, 5);
  }, [homeHeaderSlideshowImages]);

  // Função para obter dados do insight selecionado
  const getInsightData = (insightValue) => {
    switch(insightValue) {
      case 'stat1':
        return { name: aboutStat1Name, value: aboutStat1Value };
      case 'stat2':
        return { name: aboutStat2Name, value: aboutStat2Value };
      case 'stat3':
        return { name: aboutStat3Name, value: aboutStat3Value };
      case 'stat4':
        return { name: aboutStat4Name, value: aboutStat4Value };
      default:
        return null;
    }
  };

  // Função para obter ícone baseado no nome do insight
  const getInsightIcon = (name) => {
    if (!name) return FaAward;
    
    const nameLower = name.toLowerCase();
    if (nameLower.includes('cliente') || nameLower.includes('satisfeito')) return FaUsers;
    if (nameLower.includes('ano') || nameLower.includes('experiência')) return FaAward;
    if (nameLower.includes('imóvel') || nameLower.includes('propriedade')) return FaHome;
    if (nameLower.includes('venda') || nameLower.includes('vendas')) return FaChartLine;
    return FaAward;
  };
  const [featuredProperties, setFeaturedProperties] = useState([]);
  const [, setCities] = useState([]);
  /** Contagens por cidade e bairro (imóveis públicos) para a seção "Imóveis por cidades ou bairros". */
  const [propertyLocationCounts, setPropertyLocationCounts] = useState({
    cities: {},
    neighborhoods: {}
  });
  const [loading, setLoading] = useState(true);
  const [isPreLoading, setIsPreLoading] = useState(true);
  const [searchParams, setSearchParams] = useState({
    search: '',
    type: '',
    purpose: '',
    location: '',
    maxPrice: ''
  });
  const [blogPosts, setBlogPosts] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  
  // Estados para o carrossel de feedbacks
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const carouselRef = useRef(null);

  // Opções para os selects
  const typeOptions = [
    { value: '', label: 'Tipo de Imóvel' },
    { value: 'house', label: 'Casa' },
    { value: 'apartment', label: 'Apartamento' },
    { value: 'loft', label: 'Loft' },
    { value: 'warehouse', label: 'Galpão' },
    { value: 'store', label: 'Loja' },
    { value: 'building', label: 'Prédio' },
    { value: 'office', label: 'Sala' },
    { value: 'land', label: 'Terreno' },
    { value: 'farm', label: 'Sítio' },
    { value: 'countryHouse', label: 'Chácara' }
  ];

  const purposeOptions = [
    { value: '', label: 'Finalidade' },
    { value: 'sale', label: 'Venda' },
    { value: 'rent', label: 'Aluguel' }
  ];

  // Estilos do React Select
  const customStyles = {
    control: (base, state) => ({
      ...base,
      borderColor: state.isFocused ? (primaryColor || 'var(--primary-color, #000000)') : '#cfd4dd',
      borderRadius: '0.5rem',
      minHeight: '46px',
      height: '46px',
      boxShadow: 'none',
      cursor: 'pointer',
      '&:hover': {
        borderColor: primaryColor || 'var(--primary-color, #000000)'
      }
    }),
    singleValue: (base, { selectProps }) => ({
      ...base,
      color: selectProps.hasValue ? '#111827' : '#6B7280'
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6b7280'
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 12px',
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? (primaryColor || 'var(--primary-color, #000000)')
        : state.isFocused 
        ? `${primaryColor || 'var(--primary-color, #000000)'}15`
        : 'white',
      color: state.isSelected ? 'white' : '#111827',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: state.isSelected 
          ? (primaryColor || 'var(--primary-color, #000000)')
          : `${primaryColor || 'var(--primary-color, #000000)'}15`
      }
    }),
    menu: (base) => ({
      ...base,
      cursor: 'pointer'
    }),
    indicatorSeparator: (base) => ({
      ...base,
      cursor: 'pointer'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      cursor: 'pointer'
    })
  };

  // Estilos para impedir drag e seleção de imagens
  const imageStyles = {
    userSelect: 'none',
    WebkitUserSelect: 'none',
    MozUserSelect: 'none',
    msUserSelect: 'none',
    pointerEvents: 'none',
    draggable: false
  };

  useEffect(() => {
    // Blog/feedbacks abaixo da dobra — não bloqueiam o first paint
    let idleId;
    let timeoutId;
    const run = () => {
      loadBlogPosts();
      loadFeedbacks();
    };
    if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(run, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(run, 400);
    }
    return () => {
      if (idleId != null && typeof window !== 'undefined' && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId != null) clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    loadMainHomeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when home location entity changes
  }, [homeLocationSpotsEntity]);

  // AOS: carrega CSS/JS só depois do preloader (fora do caminho crítico)
  useEffect(() => {
    if (isPreLoading) return undefined;
    let cancelled = false;
    (async () => {
      const [{ default: AOS }] = await Promise.all([
        import('aos'),
        import('aos/dist/aos.css'),
      ]);
      if (cancelled) return;
      AOS.init({
        duration: 1000,
        easing: 'ease-out-cubic',
        once: true,
        offset: 100
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [isPreLoading]);

  useEffect(() => {
    if (!loading) {
      setIsPreLoading(false);
    }
  }, [loading]);

  const loadMainHomeData = async () => {
    setLoading(true);
    try {
      // Destaques: query enxuta (showInHome) — não baixa o catálogo inteiro
      const featured = await fetchHomeFeaturedProperties(8);
      setFeaturedProperties(featured);
      setLoading(false);

      // Contagens da seção bento: diferidas (podem usar catálogo em cache)
      const locationRows =
        homeLocationSpotsEntity === 'condominiums'
          ? await fetchCondominiumsCatalog({ scope: 'public' })
          : await fetchPropertiesCatalog({ scope: 'public' });
      const cityCounts = {};
      const neighborhoodCounts = {};
      locationRows.forEach((d) => {
        if (d.isInactive === true || d.isAvailable === false) return;
        const c = d.city && String(d.city).trim();
        if (c) cityCounts[c] = (cityCounts[c] || 0) + 1;
        const nb = d.neighborhood && String(d.neighborhood).trim();
        if (nb) neighborhoodCounts[nb] = (neighborhoodCounts[nb] || 0) + 1;
      });
      setPropertyLocationCounts({ cities: cityCounts, neighborhoods: neighborhoodCounts });
      const uniqueCities = [...new Set(Object.keys(cityCounts))].sort((a, b) =>
        a.localeCompare(b, 'pt-BR')
      );
      setCities(uniqueCities);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setLoading(false);
    }
  };

  // Função para finalizar o pre-loading
  const handlePreLoadingComplete = useCallback(() => {
    setIsPreLoading(false);
  }, []);

  const loadBlogPosts = async () => {
    try {
      const db = getFirestore();
      // Primeiro pegamos todos os posts publicados sem ordenação
      const q = query(
        collection(db, 'blogPosts'),
        where('status', '==', 'published')
      );
      
      const snapshot = await getDocs(q);
      const posts = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        // Ordenamos manualmente por data
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate() || new Date(0);
          const dateB = b.createdAt?.toDate() || new Date(0);
          return dateB - dateA;
        })
        // Pegamos apenas os 3 primeiros
        .slice(0, 3);
      
      setBlogPosts(posts);
    } catch (error) {
      console.error('Erro ao carregar posts do blog:', error);
    }
  };

  const loadFeedbacks = async () => {
    try {
      const db = getFirestore();
      const q = query(
        collection(db, 'feedbacks'),
        orderBy('createdAt', 'desc'),
        limit(24)
      );
      
      const snapshot = await getDocs(q);
      const feedbacksData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setFeedbacks(feedbacksData);
    } catch (error) {
      console.error('Erro ao carregar feedbacks:', error);
    }
  };

  // Efeito para o carrossel automático
  React.useEffect(() => {
    if (feedbacks.length > 1 && !isHovered && !isDragging) {
      const interval = setInterval(() => {
        setCurrentIndex(prevIndex => {
          // Mobile: 1 card, Tablet: 2 cards, Desktop: 3 cards
          const isMobile = window.innerWidth < 768;
          const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
          const cardsPerView = isMobile ? 1 : isTablet ? 2 : 3;
          const nextIndex = prevIndex + 1;
          return nextIndex >= feedbacks.length - (cardsPerView - 1) ? 0 : nextIndex;
        });
      }, 4000);

      return () => clearInterval(interval);
    }
  }, [feedbacks.length, isHovered, isDragging]);

  // Funções para arrastar
  const handleStart = (clientX) => {
    setIsDragging(true);
    setStartX(clientX);
    setScrollLeft(currentIndex);
  };

  const handleMove = (clientX) => {
    if (!isDragging) return;
    const x = clientX;
    const isMobile = window.innerWidth < 768;
    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
    const cardsPerView = isMobile ? 1 : isTablet ? 2 : 3;
    const walk = (startX - x) / 400; // 400px é aproximadamente a largura de um card
    const newIndex = Math.max(0, Math.min(feedbacks.length - cardsPerView, scrollLeft + walk));
    setCurrentIndex(newIndex);
  };

  const handleEnd = () => {
    setIsDragging(false);
    setCurrentIndex(Math.round(currentIndex));
  };

  // Event handlers para mouse
  const handleMouseDown = (e) => {
    e.preventDefault();
    handleStart(e.clientX);
  };

  const handleMouseMove = (e) => {
    handleMove(e.clientX);
  };

  const handleMouseUp = () => {
    handleEnd();
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // Event handlers para touch
  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };













  const handleSearch = () => {
    const queryParams = new URLSearchParams();
    
    if (searchParams.search.trim()) {
      queryParams.set('search', searchParams.search.trim());
    }
    if (searchParams.type) {
      queryParams.set('type', searchParams.type);
    }
    if (searchParams.purpose) {
      queryParams.set('purpose', searchParams.purpose);
    }
    if (searchParams.location && String(searchParams.location).trim()) {
      queryParams.set('location', String(searchParams.location).trim());
    }
    if (searchParams.maxPrice) {
      queryParams.set('maxPrice', String(searchParams.maxPrice));
    }

    trackSearch({
      searchTerm: searchParams.search?.trim() || undefined,
      filters: {
        type: searchParams.type,
        purpose: searchParams.purpose,
        location: searchParams.location,
        maxPrice: searchParams.maxPrice,
      },
    });

    navigate(`/propriedades?${queryParams.toString()}`);
  };

  const handleSearchSubmit = () => {
    handleSearch();
  };

  return (
    <div className="min-h-screen" style={{ fontFamily: 'Montserrat, sans-serif' }}>
      {/* PreLoader Component — sempre na Home (inclusive navegação interna) */}
      <PreLoader 
        forceShow
        isLoading={isPreLoading} 
        onLoadingComplete={handlePreLoadingComplete}
      />
      
      {/* SEO Component */}
      <SEO 
        title={homePageTitle}
        description={seoMetaDescription || (aboutHeroDescription ? aboutHeroDescription.replace(/<[^>]*>/g, '').substring(0, 160) : undefined)}
        keywords={seoKeywords}
        ogTitle={seoOgTitle}
        ogDescription={seoOgDescription}
        ogImage={seoOgImage}
        canonicalUrl={seoCanonicalUrl}
        pageType="website"
        includeSiteSchemas
      />
      
      {/* Estilos globais para impedir drag e seleção de imagens */}
      <style>
        {`
          img, .lazy-load-image-background {
            user-select: none !important;
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            pointer-events: none !important;
          }
          img {
            -webkit-user-drag: none !important;
            -khtml-user-drag: none !important;
            -moz-user-drag: none !important;
            -o-user-drag: none !important;
            user-drag: none !important;
          }
        `}
      </style>
      
      
      {/* Hero — Padrão | Editorial (Cookbook) | Luxo (cinematográfico) */}
      <Suspense fallback={<div className="h-screen md:h-[75vh] min-h-[500px] bg-gray-900" aria-hidden="true" />}>
      {resolvedHomeHeroLayout === 'editorial' ? (
        <HomeHeroEditorial
          homeHeaderMediaType={homeHeaderMediaType}
          homeHeaderImage={homeHeaderImage}
          homeHeaderImagePresentation={homeHeaderImagePresentation}
          homeHeaderSlideshowUrls={homeHeaderSlideshowUrls}
          homeHeaderVideo={homeHeaderVideo}
          homeHeaderYoutubeUrl={homeHeaderYoutubeUrl}
          homeTitle={homeTitle}
          homeSubtitle={homeSubtitle}
          primaryColor={primaryColor}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          typeOptions={typeOptions}
          purposeOptions={purposeOptions}
          customStyles={customStyles}
          onSearchSubmit={handleSearchSubmit}
          navigate={navigate}
        />
      ) : resolvedHomeHeroLayout === 'luxo' ? (
        <HomeHeroLuxury
          homeHeaderMediaType={homeHeaderMediaType}
          homeHeaderImage={homeHeaderImage}
          homeHeaderImagePresentation={homeHeaderImagePresentation}
          homeHeaderSlideshowUrls={homeHeaderSlideshowUrls}
          homeHeaderVideo={homeHeaderVideo}
          homeHeaderYoutubeUrl={homeHeaderYoutubeUrl}
          homeTitle={homeTitle}
          homeSubtitle={homeSubtitle}
          primaryColor={primaryColor}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          typeOptions={typeOptions}
          purposeOptions={purposeOptions}
          customStyles={customStyles}
          onSearchSubmit={handleSearchSubmit}
          navigate={navigate}
        />
      ) : resolvedHomeHeroLayout === 'barraFlutuante' ? (
        <HomeHeroFloatingBar
          homeHeaderMediaType={homeHeaderMediaType}
          homeHeaderImage={homeHeaderImage}
          homeHeaderImagePresentation={homeHeaderImagePresentation}
          homeHeaderSlideshowUrls={homeHeaderSlideshowUrls}
          homeHeaderVideo={homeHeaderVideo}
          homeHeaderYoutubeUrl={homeHeaderYoutubeUrl}
          homeTitle={homeTitle}
          homeSubtitle={homeSubtitle}
          primaryColor={primaryColor}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          typeOptions={typeOptions}
          purposeOptions={purposeOptions}
          customStyles={customStyles}
          onSearchSubmit={handleSearchSubmit}
          navigate={navigate}
        />
      ) : resolvedHomeHeroLayout === 'mapaMapbox' ? (
        <HomeHeroMapbox
          mapboxToken={mapSettings.mapboxToken}
          mapboxStyle={mapSettings.mapboxStyle}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          typeOptions={typeOptions}
          purposeOptions={purposeOptions}
          customStyles={customStyles}
          primaryColor={primaryColor}
          navigate={navigate}
        />
      ) : (
      <section className="mt-0 pt-20 relative h-screen md:h-[75vh] lg:h-screen min-h-[500px] md:min-h-[450px] lg:min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background base */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ 
            backgroundImage: `url(${heroBg})` 
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50" />
        </div>

        {/* Mídia personalizada baseada no tipo selecionado */}
        {homeHeaderMediaType === 'image' &&
          homeHeaderImagePresentation === 'slideshow' &&
          homeHeaderSlideshowUrls.length > 0 && (
            <div className="absolute inset-0 z-[1]">
              <HomeHeroKenBurnsSlideshow images={homeHeaderSlideshowUrls} className="z-[1]" />
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50 z-[2] pointer-events-none" />
            </div>
          )}

        {homeHeaderMediaType === 'image' &&
          (homeHeaderImagePresentation !== 'slideshow' || homeHeaderSlideshowUrls.length === 0) &&
          homeHeaderImage && (
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat z-[1]"
              style={{
                backgroundImage: `url(${homeHeaderImage})`
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50" />
            </div>
          )}

        {homeHeaderMediaType === 'video' && homeHeaderVideo && (
          <div className="absolute inset-0 overflow-hidden">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            >
              <source src={homeHeaderVideo} type={homeHeaderVideo.endsWith('.webm') ? 'video/webm' : 'video/mp4'} />
            </video>
          </div>
        )}

        {homeHeaderMediaType === 'youtube' && homeHeaderYoutubeUrl && (() => {
          const videoId = getYouTubeVideoId(homeHeaderYoutubeUrl);
          return videoId ? (
            <div className="absolute inset-0 overflow-hidden">
              <iframe
                title="Vídeo de fundo do hero"
                key={`${videoId}-${youtubeQuality}`} // Force re-render when quality changes
                src={createYouTubeUrl(videoId, youtubeQuality)}
                className="absolute top-1/2 left-1/2 w-[177.77vh] h-[56.25vw] min-w-full min-h-full transform -translate-x-1/2 -translate-y-1/2"
                style={{
                  width: '177.77vh', // 16:9 aspect ratio width
                  height: '56.25vw', // 16:9 aspect ratio height
                  minWidth: '100%',
                  minHeight: '100%'
                }}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowFullScreen
                onLoad={() => {
                  // Reset quality to highest when video loads successfully
                  setYoutubeQuality('hd1080');
                }}
                onError={() => {
                  // Try next quality in fallback chain
                  const currentIndex = qualityFallback.indexOf(youtubeQuality);
                  if (currentIndex < qualityFallback.length - 1) {
                    setYoutubeQuality(qualityFallback[currentIndex + 1]);
                  }
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/50" />
            </div>
          ) : null;
        })()}
        
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
           <h1 
             className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-montserrat font-semibold text-white mb-4 sm:mb-6"
             data-aos="fade-up"
             data-aos-delay="200"
           >
             {homeTitle || 'Encontre o imóvel dos seus sonhos'}
           </h1>
           <p 
             className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-200 mb-6 sm:mb-8 lg:mb-10"
             data-aos="fade-up"
             data-aos-delay="400"
           >
            {homeSubtitle || 'Busque entre centenas de imóveis disponíveis em nossa plataforma'}
          </p>

          <div data-aos="fade-up" data-aos-delay="600">
            <HomeHeroSearchForm
              searchParams={searchParams}
              setSearchParams={setSearchParams}
              typeOptions={typeOptions}
              purposeOptions={purposeOptions}
              customStyles={customStyles}
              primaryColor={primaryColor}
              onSubmit={handleSearchSubmit}
              navigate={navigate}
            />
          </div>
        </div>
      </section>
      )}
      </Suspense>

      {/* Featured Properties Section — padding-top extra quando a busca flutuante ocupa metade da faixa branca */}
      <section
        className={`bg-gray-50 overflow-hidden ${
          heroOverlapBottomPadding
            ? 'pt-36 pb-16 sm:pt-32 lg:pt-28'
            : 'py-16'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-3xl font-montserrat font-semibold mb-4">Imóveis em Destaque</h2>
            <p className="text-gray-600">
              Confira nossa seleção especial de imóveis em destaque
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featuredProperties.slice(0, 8).map((property, index) => (
              <div 
                key={property.id} 
                className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="h-52 relative overflow-hidden">
                  <div className="w-full h-full transform transition-transform duration-700 hover:scale-110">
                    <Link
                      to={buildPropertyPath(property)}
                      className="relative block"
                      aria-label={`Ver detalhes: ${property.title}`}
                    >
                      <LazyLoadImage
                        src={property.coverImage || property.images[0]}
                        alt={generatePropertyImageAlt(property)}
                        className="w-full h-full object-cover"
                        wrapperClassName="w-full h-52 block bg-gray-100"
                        effect="blur"
                        placeholder={
                          <div className="w-full h-52 bg-gray-200 animate-pulse"></div>
                        }
                        style={imageStyles}
                        draggable={false}
                      />
                    </Link>
                  </div>
                  <div className="bg-gray-900/50 absolute top-2 left-2 backdrop-blur-sm rounded pointer-events-none">
                    <span className="text-white px-3 py-1 text-sm rounded">
                      {property.purpose === 'sale' ? 'Venda' : 'Aluguel'}
                    </span>
                  </div>
                  <div className="bg-gray-900/50 absolute bottom-2 left-2 backdrop-blur-sm rounded pointer-events-none">
                    <span className="text-white px-3 py-1 text-md font-medium rounded">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(property.price)}
                    </span>
                  </div>
                </div>
                
                <div className="px-4 pt-2 flex-1">
                  <h3 className="text-lg font-semibold mb-2">
                    <Link
                      to={buildPropertyPath(property)}
                      className="hover:text-blue-600 transition-colors"
                    >
                      {property.title}
                    </Link>
                  </h3>
                </div>

                <div className="mt-auto">
                  <div className="px-4">
                    <div className="ont-lato text-gray-600 mb-4 flex items-center gap-2 text-[13px]">
                    <FaMapMarkerAlt className="w-5 h-5" />
                      <span>{property.address}</span>
                    </div>

                    <div className="grid grid-cols-4 gap-2 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <i className="flaticon-bed text-base"></i>
                        <span>{property.bedrooms}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="flaticon-bathtub text-base"></i>
                        <span>{property.bathrooms}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="flaticon-car text-base"></i>
                        <span>{property.parkingSpaces}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="flaticon-square text-base"></i>
                        <span>{property.area}m²</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 px-4 pb-4">
                    <Link
                      to={buildPropertyPath(property)}
                      className={outlineCtaClassName}
                      style={outlineCtaStyle}
                      {...outlineCtaHoverHandlers}
                    >
                      Mais detalhes
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HomeLocationSpotsSection
        mode={homeLocationSpotsMode === 'neighborhood' ? 'neighborhood' : 'city'}
        entityType={homeLocationSpotsEntity === 'condominiums' ? 'condominiums' : 'properties'}
        spots={homeLocationSpots}
        countMap={
          homeLocationSpotsMode === 'neighborhood'
            ? propertyLocationCounts.neighborhoods
            : propertyLocationCounts.cities
        }
        primaryColor={primaryColor}
      />

      {/* What We Do Section */}
      <section className="py-16 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <h2 
            className="text-3xl font-montserrat font-semibold text-center mb-12"
            data-aos="fade-up"
          >
            O Que Fazemos?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4">
            <div 
              className="p-6 bg-white rounded-lg shadow-md overflow-hidden border border-gray-300 transition-all duration-500 hover:border-transparent hover:shadow-xl group"
              data-aos="fade-right"
              data-aos-delay="200"
            >
              <div className="flex justify-center">
                <img 
                  src={require('../Compre Uma Casa.png')} 
                  alt="Compre Uma Casa" 
                  style={imageStyles}
                  draggable={false}
                />
              </div>
              <div className="p-6 md:p-0 md:pt-6 text-center">
                <h3 className="font-montserrat font-semibold text-xl mb-4">Compre uma Casa</h3>
                <p className="text-gray-600 mb-6">
                  Encontre a casa dos seus sonhos com nossa ampla seleção de propriedades.
                </p>
                <Link
                  to="/propriedades?purpose=sale"
                  className={`${outlineCtaClassName} w-auto`}
                  style={outlineCtaStyle}
                  {...outlineCtaHoverHandlers}
                >
                  Ver Mais
                </Link>
              </div>
            </div>

            <div 
              className="p-6 bg-white rounded-lg shadow-md overflow-hidden border border-gray-300 transition-all duration-500 hover:border-transparent hover:shadow-xl group"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              <div className="flex justify-center">
                <img 
                  src={require('../Alugue Uma Casa.png')} 
                  alt="Alugue Uma Casa" 
                  style={imageStyles}
                  draggable={false}
                />
              </div>
              <div className="p-6 md:p-0 md:pt-6 text-center">
                <h3 className="font-montserrat font-semibold text-xl mb-4">Alugue uma Casa</h3>
                <p className="text-gray-600 mb-6">
                  Encontre o lugar perfeito para morar com nossas opções de aluguel.
                </p>
                <Link
                  to="/propriedades?purpose=rent"
                  className={`${outlineCtaClassName} w-auto`}
                  style={outlineCtaStyle}
                  {...outlineCtaHoverHandlers}
                >
                  Ver Mais
                </Link>
              </div>
            </div>

            <div 
              className="p-6 bg-white rounded-lg shadow-md overflow-hidden border border-gray-300 transition-all duration-500 hover:border-transparent hover:shadow-xl group"
              data-aos="fade-left"
              data-aos-delay="600"
            >
              <div className="flex justify-center">
                <img 
                  src={require('../Venda Sua Casa.png')} 
                  alt="Venda Sua Casa" 
                  style={imageStyles}
                  draggable={false}
                />
              </div>
              <div className="p-6 md:p-0 md:pt-6 text-center">
                <h3 className="font-montserrat font-semibold text-xl mb-4">Venda sua Casa</h3>
                <p className="text-gray-600 mb-6">
                  Anuncie seu imóvel e encontre o comprador ideal com nossa ajuda.
                </p>
                <a
                  href="https://wa.me/5511999999999"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-3 rounded-full font-medium transition-all duration-300 text-center"
                  style={{
                    backgroundColor: 'var(--btn-outline-bg, #ffffff)',
                    color: 'var(--btn-outline-text, #3B82F6)',
                    border: '1px solid var(--btn-outline-border, #3B82F6)',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-hover-bg, #3B82F6)';
                    e.currentTarget.style.color = 'var(--btn-outline-hover-text, #ffffff)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--btn-outline-bg, #ffffff)';
                    e.currentTarget.style.color = 'var(--btn-outline-text, #3B82F6)';
                  }}
                >
                  Entre em contato
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 overflow-visible">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div 
              className="rounded-lg overflow-visible group w-full relative order-1 lg:order-1"
              data-aos="fade-right"
            >
              <img
                src={aboutHeroImage || require('../back.jpg')}
                alt={aboutHeroTitle || "Benefícios"}
                className="card-cover-img card-cover-img--rounded"
              />
              
              {/* Cards sobrepostos */}
              {aboutHeroInsight1 && getInsightData(aboutHeroInsight1) && (
                <div className="absolute bottom-0 left-0 lg:-bottom-6 lg:-left-6 bg-white rounded-xl p-2 lg:p-4 shadow-xl transform hover:scale-110 hover:-rotate-2 transition-all duration-500 animate-float">
                  <div className="flex items-center space-x-2 lg:space-x-4">
                    <div className="p-2 lg:p-3 bg-blue-100 rounded-lg">
                      {React.createElement(getInsightIcon(getInsightData(aboutHeroInsight1).name), { className: "w-4 h-4 lg:w-6 lg:h-6 text-blue-400" })}
                    </div>
                    <div>
                      <p className="text-base lg:text-2xl font-bold text-gray-900 animate-count-up">{getInsightData(aboutHeroInsight1).value}</p>
                      <p className="text-xs lg:text-sm text-gray-600 whitespace-nowrap">{getInsightData(aboutHeroInsight1).name}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {aboutHeroInsight2 && getInsightData(aboutHeroInsight2) && (
                <div className="absolute top-0 right-0 lg:-top-6 lg:-right-6 bg-white rounded-xl p-2 lg:p-4 shadow-xl transform hover:scale-110 hover:rotate-2 transition-all duration-500 animate-float-delayed">
                  <div className="flex items-center space-x-2 lg:space-x-4">
                    <div className="p-2 lg:p-3 bg-green-100 rounded-lg animate-pulse">
                      {React.createElement(getInsightIcon(getInsightData(aboutHeroInsight2).name), { className: "w-4 h-4 lg:w-6 lg:h-6 text-green-600" })}
                    </div>
                    <div>
                      <p className="text-base lg:text-2xl font-bold text-gray-900 animate-count-up-delayed">{getInsightData(aboutHeroInsight2).value}</p>
                      <p className="text-xs lg:text-sm text-gray-600 whitespace-nowrap">{getInsightData(aboutHeroInsight2).name}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            <div className="space-y-8 order-2 lg:order-2" data-aos="fade-left" data-aos-delay="200">
              <h2 className="text-3xl font-montserrat font-semibold">
                {aboutHeroTitle || (companyName ? `Por Que Escolher ${companyName.toUpperCase()}` : 'Por Que Nos Escolher')}
              </h2>
              
              <div className="space-y-6">
                {aboutHeroDescription ? (
                  <div 
                    className="text-gray-600 formatted-content"
                    dangerouslySetInnerHTML={{ __html: aboutHeroDescription }}
                  />
                ) : (
                  <>
                <div className="flex gap-4">
                  <div className="text-blue-500">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Experiência comprovada</h3>
                    <p className="text-gray-600">Anos de experiência no mercado imobiliário.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-blue-500">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Soluções Personalizadas</h3>
                    <p className="text-gray-600">Atendimento personalizado para cada cliente.</p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-blue-500">
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Preços transparentes</h3>
                    <p className="text-gray-600">Sem taxas ocultas ou surpresas desagradáveis.</p>
                  </div>
                </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA - Call to action */}
      <CtaBanner
        enabled={ctaEnabled}
        ctaLayout={ctaLayout}
        ctaTitle={ctaTitle}
        ctaDescription={ctaDescription}
        ctaButtonText={ctaButtonText}
        ctaImageSource={ctaImageSource}
        ctaSystemImage={ctaSystemImage}
        ctaImageStyle={ctaImageStyle}
        ctaImage={ctaImage}
        primaryColor={primaryColor}
      />

      {/* Testimonials Section */}
      <section className="py-16 bg-gray-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          {/* Header com título, descrição e botões de navegação */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-12" data-aos="fade-up">
            <div className="lg:flex-1 mb-8 lg:mb-0">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-4 text-center lg:text-left">
                {feedbacksTitle || 'Histórias reais, sucesso real'}
              </h2>
              {feedbacksDescription && (
                <div 
                  className="text-base md:text-lg text-black leading-relaxed text-center lg:text-left"
                  dangerouslySetInnerHTML={{ __html: feedbacksDescription }}
                />
              )}
            </div>
            
            {/* Botões de navegação */}
            {feedbacks.length > 1 && (
              <div className="flex gap-2 justify-center lg:justify-end">
                <button
                  onClick={() => {
                    setCurrentIndex(Math.max(0, currentIndex - 1));
                  }}
                  className="w-12 h-12 border rounded-lg flex items-center justify-center transition-all duration-300 ease-in-out"
                  style={{
                    backgroundColor: 'var(--secondary-color, #ffffff)',
                    borderColor: 'var(--primary-color, #3B82F6)',
                    color: 'var(--primary-color, #3B82F6)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--primary-color, #3B82F6)';
                    e.target.style.color = 'var(--secondary-color, #ffffff)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--secondary-color, #ffffff)';
                    e.target.style.color = 'var(--primary-color, #3B82F6)';
                  }}
                  disabled={currentIndex === 0}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    const isMobile = window.innerWidth < 768;
                    const isTablet = window.innerWidth >= 768 && window.innerWidth < 1024;
                    const cardsPerView = isMobile ? 1 : isTablet ? 2 : 3;
                    setCurrentIndex(Math.min(feedbacks.length - cardsPerView, currentIndex + 1));
                  }}
                  className="w-12 h-12 border rounded-lg flex items-center justify-center transition-all duration-300 ease-in-out"
                  style={{
                    backgroundColor: 'var(--secondary-color, #ffffff)',
                    borderColor: 'var(--primary-color, #3B82F6)',
                    color: 'var(--primary-color, #3B82F6)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--primary-color, #3B82F6)';
                    e.target.style.color = 'var(--secondary-color, #ffffff)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--secondary-color, #ffffff)';
                    e.target.style.color = 'var(--primary-color, #3B82F6)';
                  }}
                  disabled={currentIndex >= feedbacks.length - (window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3)}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          
          {/* Grid de Feedbacks - Responsivo */}
          <div 
            className="relative overflow-hidden" 
            data-aos="fade-up" 
            data-aos-delay="200"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
              setIsHovered(false);
              handleMouseLeave();
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {feedbacks.length > 0 ? (
              <div 
                ref={carouselRef}
                className="flex transition-transform duration-500 ease-out cursor-grab active:cursor-grabbing select-none"
                style={{
                  transform: `translateX(-${currentIndex * (window.innerWidth < 768 ? 100 : window.innerWidth < 1024 ? 50 : 33.33)}%)`,
                  transition: isDragging ? 'none' : 'transform 500ms ease-out'
                }}
              >
                {feedbacks.map((feedback, index) => (
                  <div key={feedback.id} className="w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-4">
                    <div className="bg-white p-8 rounded-lg border border-gray-200 relative">
                      
                      {/* Foto do cliente */}
                      <div className="flex items-center gap-4 mb-6">
                        {feedback.image ? (
                          <img
                            src={feedback.image}
                            alt={feedback.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center">
                            <svg className="w-8 h-8 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                          </div>
                        )}
                        <div>
                          <h4 className="text-xl font-bold text-black">{feedback.name}</h4>
                          <p className="text-sm text-gray-600">Cliente desde {feedback.clientSince || new Date().getFullYear()}</p>
                        </div>
                      </div>
                      
                      {/* Comentário */}
                      <p className="text-black leading-relaxed mb-6 relative z-10">
                        {feedback.comment}
                      </p>
                      
                      {/* Avaliação com estrelas */}
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(star => (
                          <svg
                            key={star}
                            className={`w-5 h-5 ${
                              star <= (feedback.rating || 5) ? 'text-yellow-500' : 'text-gray-300'
                            }`}
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              // Fallback para quando não há feedbacks cadastrados
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white p-8 rounded-lg border border-gray-200 relative">
                    
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-full bg-gray-200" />
                      <div>
                        <h4 className="text-xl font-bold text-black">Cliente {i}</h4>
                        <p className="text-sm text-gray-600">Cliente desde 2024</p>
                      </div>
                    </div>
                    
                    <p className="text-black leading-relaxed mb-6 relative z-10">
                      "Excelente experiência! O atendimento foi muito profissional e encontrei
                      exatamente o que procurava."
                    </p>
                    
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map(star => (
                        <svg
                          key={star}
                          className="w-5 h-5 text-yellow-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <h2 
            className="text-3xl font-montserrat font-semibold text-center mb-12"
            data-aos="fade-up"
          >
            Do Nosso Blog
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <article 
                key={post.id} 
                className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow group"
                data-aos="fade-up"
                data-aos-delay={index * 200}
              >
                <Link
                  to={`/blog/${post.slug || post.id}`}
                  className="block text-inherit no-underline"
                >
                  <div className="h-48 relative overflow-hidden">
                    <LazyLoadImage
                      src={post.imageUrl}
                      alt={post.title}
                      className="card-cover-img"
                      effect="blur"
                      wrapperProps={{
                        className: "w-full h-full absolute inset-0"
                      }}
                      style={imageStyles}
                      draggable={false}
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-blue-500 text-sm">{post.category}</span>
                    <h3 className="font-semibold text-xl mt-2 mb-3 group-hover:text-blue-500 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {post.content}
                    </p>
                    <span 
                      className="text-blue-500 hover:text-blue-600 inline-flex items-center"
                    >
                      Ler mais 
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </span>
                  </div>
                </Link>
              </article>
            ))}
          </div>

          <div className="text-center mt-12" data-aos="fade-up" data-aos-delay="800">
            <Link
              to="/blog"
              className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md transition-colors duration-200 hover:opacity-90"
              style={{
                backgroundColor: 'var(--primary-color, #3B82F6)',
                color: 'var(--secondary-color, #ffffff)'
              }}
            >
              Ver mais posts
            </Link>
          </div>
        </div>
      </section>

      <ScrollToTop />
      
      {/* Animações CSS para os cards */}
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes floatDelayed {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-15px);
          }
        }
        
        @keyframes countUp {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        
        @keyframes countUpDelayed {
          from {
            opacity: 0;
            transform: scale(0.3) rotate(-10deg);
          }
          to {
            opacity: 1;
            transform: scale(1) rotate(0deg);
          }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        
        .animate-float-delayed {
          animation: floatDelayed 4s ease-in-out infinite;
          animation-delay: 1s;
        }
        
        .animate-count-up {
          animation: countUp 1s ease-out forwards;
          animation-delay: 0.5s;
        }
        
        .animate-count-up-delayed {
          animation: countUpDelayed 1.2s ease-out forwards;
          animation-delay: 1s;
        }
      `}</style>
    </div>
  );
}

export default Home; 